/**
 * ImportSeedsModal Component
 *
 * Modal for importing seeds from various file formats.
 * Uses the same import actions as ImportAssetsModal.
 * Supports seed import from XLSX, CSV, or TXT files.
 *
 * @module sections/seeds/components/modals
 */

import { useState } from 'react';
import { toast } from 'sonner';

import { ImportAction, ImportModal } from '@/components/import';
import { SEED_IMPORT_ACTIONS } from '@/components/import/constants/importActions';
import { useAxios } from '@/hooks/useAxios';
import { useUploadFile } from '@/hooks/useFiles';

import {
  convertConfigsToSettings,
  sanitizeConfigKeys,
} from './utils/configValidation';

export interface ImportSeedsModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Optional callback when import is confirmed with action and file */
  onImport?: (action: ImportAction, file: File) => void;
}

/**
 * ImportSeedsModal Component
 *
 * Displays a multi-stage modal for importing seeds from various file formats.
 * Includes file upload, validation, and preview stages for supported formats.
 *
 * @param props - Component props
 * @returns Import modal JSX
 *
 * @example
 * ```tsx
 * <ImportSeedsModal
 *   open={showImportModal}
 *   onClose={() => setShowImportModal(false)}
 *   onImport={(action, file) => {
 *     console.log('Import from:', action.value);
 *     console.log('File:', file.name);
 *   }}
 * />
 * ```
 */
export function ImportSeedsModal({
  open,
  onClose,
  onImport,
}: ImportSeedsModalProps) {
  const axios = useAxios();
  const { mutateAsync: uploadFile } = useUploadFile();

  // State for configuration options
  const [selectedConfigs, setSelectedConfigs] = useState<
    Record<string, boolean | string>
  >({});

  /**
   * Handle configuration option changes
   * Updates selectedConfigs state when user toggles checkbox or changes input
   */
  const handleConfigChange = (key: string, value: boolean | string) => {
    setSelectedConfigs(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * Handle file selection - uploads to S3 immediately
   * Returns the S3 file path for later use
   */
  const handleFileSelected = async (
    action: ImportAction,
    file: File
  ): Promise<string> => {
    // Step 1: Construct the file path
    const filePath = `imports/${action.value}/${file.name}`;

    // Step 2: Convert file to ArrayBuffer for upload
    const arrayBuffer = await file.arrayBuffer();

    // Step 3: Upload file to S3 via presigned URL
    const uploadPromise = uploadFile({
      name: filePath,
      content: arrayBuffer,
      ignoreSnackbar: true, // We'll handle toasts manually
    });

    await toast.promise(uploadPromise, {
      loading: `Uploading ${file.name}...`,
      success: `${file.name} uploaded successfully`,
      error: `Failed to upload ${file.name}`,
    });

    return filePath;
  };

  /**
   * Handle import confirmation - triggers backend processing
   * File is already uploaded to S3 at this point
   */
  const handleImport = async (
    action: ImportAction,
    file: File,
    filePath: string
  ) => {
    try {
      // Security: Sanitize config keys against allowlist to prevent prototype pollution
      // This validates that only approved config keys are included in the API payload
      const sanitizedConfigs = sanitizeConfigKeys(selectedConfigs);

      // Convert sanitized configs to API settings format
      const settings = convertConfigsToSettings(sanitizedConfigs);

      // Trigger import via POST /account/<action>-import
      const importPromise = axios.post(`/account/${action.value}-import`, {
        secret: {},
        value: filePath,
        settings: settings, // NEW: Include configuration settings
      });

      await toast.promise(importPromise, {
        loading: `Processing ${action.label} import...`,
        success: `Import from ${action.label} completed successfully`,
        error: `Failed to process ${action.label} import`,
      });

      // Call optional callback
      if (onImport) {
        onImport(action, file);
      }

      // Close modal on success
      onClose();
    } catch (error) {
      console.error('Import failed:', error);
      // Toast errors are already shown by toast.promise
    }
  };

  return (
    <ImportModal
      open={open}
      onClose={onClose}
      title="Import Seeds"
      actions={SEED_IMPORT_ACTIONS}
      onFileSelected={handleFileSelected}
      onImport={handleImport}
      selectedConfigs={selectedConfigs}
      onConfigChange={handleConfigChange}
    />
  );
}
